import React, { useEffect, useState } from 'react';
import { useContracts } from '../../hooks/useContract';
import { formatUsdc, formatRelative, bnToNumber } from '../../utils/format';
import { ACTIVITY_TYPES, ACTIVITY_ICONS, PACKAGE_NAMES, RANK_NAMES } from '../../utils/constants';
import { InlineLoader } from '../common/Loader';
import { EmptyState } from '../common/StatBox';

// Home version — shows last 5 + "View More" toggle that expands to paginated list.
export const RecentActivity = ({ userId }) => {
  const { viewsR } = useContracts();
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const PAGE_SIZE = expanded ? 10 : 5;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR || !userId) return;
      try {
        const res = await viewsR.getUserActivities(userId, expanded ? page : 0, PAGE_SIZE);
        if (cancelled) return;
        setItems(res.items);
        setTotal(bnToNumber(res.total));
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR, userId, expanded, page]);

  if (!items) return <InlineLoader />;
  if (items.length === 0) return <EmptyState icon="📜" title="No activity yet" text="Your transactions will appear here." />;

  const totalPages = expanded ? Math.ceil(total / PAGE_SIZE) : 1;

  return (
    <div>
      {items.map((a, i) => {
        const type = bnToNumber(a.activityType);
        const label = ACTIVITY_TYPES[type] || 'Unknown';
        const icon = ACTIVITY_ICONS[type] || '•';
        const meta = [];
        if (type === 1 || type === 2) meta.push(PACKAGE_NAMES[a.packageId] || `Pkg ${a.packageId}`);
        if (type === 3 || type === 6) meta.push(`from #${a.fromId.toString()}`);
        if (type === 4) meta.push(`${RANK_NAMES[bnToNumber(a.packageId) + 1] || ''} pool`);
        if (type === 7) meta.push(`Rank: ${RANK_NAMES[bnToNumber(a.amount)] || ''}`);
        meta.push(formatRelative(a.timestamp));
        const isDebit = type === 5 || type === 1;
        const showAmt = type !== 7;

        return (
          <div key={i} className="activity-compact">
            <div className="ic-box">{icon}</div>
            <div className="info">
              <div className="type">{label}</div>
              <div className="meta">{meta.join(' · ')}</div>
            </div>
            {showAmt && (
              <div className={`amt ${isDebit ? 'debit' : ''}`}>
                {isDebit ? '-' : '+'}${formatUsdc(a.amount)}
              </div>
            )}
          </div>
        );
      })}

      {!expanded && total > 5 && (
        <button className="view-more-btn" onClick={() => setExpanded(true)}>
          View More ({total} total) →
        </button>
      )}

      {expanded && totalPages > 1 && (
        <div className="flex flex-center mt-16" style={{ gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>← Prev</button>
          <span style={{ padding: '0 8px', color: 'var(--text-secondary)', fontSize: 12 }}>
            {page + 1} / {totalPages}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>Next →</button>
        </div>
      )}
    </div>
  );
};
